import React, { useState, useEffect, CSSProperties } from "react";
import { Table, OverlayTrigger, Tooltip, Modal, Row } from "react-bootstrap";
import "jquery-confirm/css/jquery-confirm.css";
import "jquery-confirm";
import { HISTORY_REPORT_STATUS } from "../../../../common/Constant";
import AccordionComponent from "../../../../common/Accordion";
import DataTable from "../../../../common/DataTable";
import {
  CASE_OVERRIDDEN_CONFIGURATION_HEADERS,
  TEAM_OVERRIDDEN_CONFIGURATION_HEADERS,
} from "../actions/constant";
import {
  getCaseBillingOverridenConfiguration,
  getTeamBillingOverridenConfiguration,
} from "../../../../services/BillingAPI";
import Loader from "../../../../common/Loader";

interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onSortAction?: (row: any, action?: string) => void;
  isLogTable?: boolean;
  isStandardBilling?: boolean;
}

const BillingDataTable: React.FC<DataTableProps> = ({
  headers,
  rows = [],
  onAction,
  onSortAction,
  isLogTable = false,
  isStandardBilling = false,
}) => {
  const [sortedRows, setSortedRows] = useState<any[]>(rows);
  const [showModal, setShowModal] = useState(false);
  const [overrideData, setOverrideData] = useState<any[]>([]);
  const [overrideTitle, setOverrideTitle] = useState<string>("");
  const [loadingOverride, setLoadingOverride] = useState(false);
  const [teamOverrideList, setTeamOverrideList] = useState<any[]>([]);
  const [caseOverrideList, setCaseOverrideList] = useState<any[]>([]);
  const [activeAccordionKeys, setActiveAccordionKeys] = useState<string[]>([
    "0",
  ]);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ASC" | "DESC" | null;
  }>({
    key: "",
    direction: null,
  });

  const handleSort = (key: string, direction: "ASC" | "DESC") => {
    if (sortConfig.key === key && sortConfig.direction === direction) {
      return; // Prevent clicking on the same active sort direction
    }
    const sorted = [...sortedRows].sort((a, b) => {
      if (direction === "ASC") {
        return a[key] > b[key] ? 1 : -1;
      }
      if (direction === "DESC") {
        return a[key] < b[key] ? 1 : -1;
      }
      return 0;
    });

    setSortedRows(sorted);
    setSortConfig({ key, direction });
    onSortAction?.({ key, direction });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setActiveAccordionKeys(["0"]);
  };

  const handleOverrideModel = async (row: any) => {
    try {
      setShowModal(true);
      setLoadingOverride(true);
      setOverrideData([]);
      setTeamOverrideList([]);
      setCaseOverrideList([]);
      setActiveAccordionKeys(["0"]);
      setOverrideTitle("");

      if (row?.billingConfigId) {
        setOverrideTitle("SYSTEM");
        const teamList = await getTeamBillingOverridenConfiguration(
          row.billingConfigId,
        );
        const caseList = await getCaseBillingOverridenConfiguration(
          row.billingConfigId,
        );
        setOverrideData(teamList || []);
        setTeamOverrideList(teamList || []);
        setCaseOverrideList(caseList || []);
        return;
      }

      if (row?.billingTeamConfigId) {
        setOverrideTitle("TEAM");
        const data = await getCaseBillingOverridenConfiguration(
          row.billingTeamConfigId,
        );
        setOverrideData(data || []);
        setCaseOverrideList(data || []);
        return;
      }
    } catch (error) {
      setOverrideData([]);
      setTeamOverrideList([]);
      setCaseOverrideList([]);
      setOverrideTitle("");
    } finally {
      setLoadingOverride(false);
    }
  };

  const accordionData = (() => {
    const items: any[] = [];

    if (overrideTitle === "SYSTEM") {
      if (teamOverrideList?.length > 0) {
        items.push({
          title: "Team Level Override",
          role: teamOverrideList?.[0]?.webRoleName,
          rows: teamOverrideList,
          headers: TEAM_OVERRIDDEN_CONFIGURATION_HEADERS,
        });
      }

      if (caseOverrideList?.length > 0) {
        items.push({
          title: "Case Level Override",
          role: caseOverrideList?.[0]?.webRoleName,
          rows: caseOverrideList,
          headers: CASE_OVERRIDDEN_CONFIGURATION_HEADERS,
        });
      }
    }

    if (overrideTitle === "TEAM") {
      if (caseOverrideList?.length > 0) {
        items.push({
          title: "Case Level Override",
          role: caseOverrideList?.[0]?.webRoleName,
          rows: caseOverrideList,
          headers: CASE_OVERRIDDEN_CONFIGURATION_HEADERS,
        });
      }
    }

    return items;
  })();

  const renderOverrideAccordion = () => (
    <AccordionComponent
      activeKey={activeAccordionKeys}
      onSelect={(eventKey: string | null) => {
        if (!eventKey) return;

        setActiveAccordionKeys((prevKeys) =>
          prevKeys.includes(eventKey)
            ? prevKeys.filter((key) => key !== eventKey)
            : [...prevKeys, eventKey],
        );
      }}
      items={accordionData?.map((item, index) => ({
        title: (
          <div className="d-flex align-items-center w-100">
            <span className="fw-semibold">
              {item.title}{" "}
              {item.role && (
                <span className="theme-color fw-normal">
                  Role ({item.role})
                </span>
              )}
            </span>
          </div>
        ),
        content: (
          <div className="recordtotal2">
            <DataTable headers={item.headers} rows={item.rows} />
          </div>
        ),
        eventKey: index.toString(),
      }))}
      isROR
    />
  );

  return (
    <>
      <div className="table-responsive border-0 mb-2">
        <Table>
          <thead>
            <tr>
              {headers.map((header) => (
                // <th key={header.key} style={thStyle}>
                <th
                  key={header.key}
                  style={{
                    border: "none",
                    padding: "8px",
                    backgroundColor: "#e0f7fa",
                    color: "#000",
                    cursor: header.sortable ? "pointer" : "default",
                    position: header.key === "actions" ? "sticky" : "sticky",
                    top: 0,
                    right: header.key === "actions" ? 0 : undefined,
                    zIndex: header.key === "actions" ? 5 : -90,

                    backgroundClip: "padding-box",

                    textAlign: ["actions"].includes(header.key)
                      ? "center"
                      : "left",
                    ...header.style,
                  }}
                >
                  <div
                    style={{
                      display: ["actions"].includes(header.key)
                        ? "flex"
                        : "flex",

                      justifyContent: ["actions"].includes(header.key)
                        ? "space-evenly"
                        : "space-between",

                      alignItems: "center",
                    }}
                  >
                    <span>{header.label}</span>
                    {header.sortable && (
                      <span
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          marginLeft: "4px",
                          cursor: "pointer",
                          position: "absolute",
                          right: "0px",
                        }}
                      >
                        {!(
                          sortConfig.key === header.key &&
                          sortConfig.direction === "ASC"
                        ) && (
                          <span
                            className="material-icons"
                            style={{
                              fontSize: "18px",
                              lineHeight: "10px",
                            }}
                            onClick={() => handleSort(header.key, "ASC")}
                          >
                            arrow_drop_up
                          </span>
                        )}
                        {!(
                          sortConfig.key === header.key &&
                          sortConfig.direction === "DESC"
                        ) && (
                          <span
                            className="material-icons"
                            style={{
                              fontSize: "18px",
                              lineHeight: "10px",
                            }}
                            onClick={() => handleSort(header.key, "DESC")}
                          >
                            arrow_drop_down
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows?.length > 0 ? (
              rows.map((row, index) => (
                <tr
                  key={row.id}
                  style={{
                    backgroundColor: index % 2 === 1 ? "white" : "#f2f2f2",
                    position: "sticky",
                  }}
                >
                  {headers.map((header) => {
                    if (header.key === "actions") {
                      return (
                        <td
                          key={header.key}
                          style={{
                            padding: "8px",
                            textAlign: "center",
                            position: "sticky",
                            right: 0,
                            backgroundColor:
                              index % 2 === 1 ? "white" : "#f2f2f2",
                            zIndex: 4,
                            backgroundClip: "padding-box",
                          }}
                        >
                          {row.actions?.View && (
                            <OverlayTrigger
                              placement="auto"
                              delay={{ show: 200, hide: 10 }}
                              overlay={
                                <Tooltip
                                  id={`view-tooltip-${row.id}`}
                                  style={{ backgroundColor: "#b3e0ff" }}
                                >
                                  View
                                </Tooltip>
                              }
                            >
                              <span
                                className="material-icons color_brand"
                                onClick={() => onAction?.(row, "View")}
                                style={{ cursor: "pointer" }}
                              >
                                visibility
                              </span>
                            </OverlayTrigger>
                          )}
                          {row.actions?.Edit && (
                            <OverlayTrigger
                              placement="auto"
                              delay={{ show: 200, hide: 10 }}
                              overlay={
                                <Tooltip
                                  id={`edit-tooltip-${row.id}`}
                                  style={{ backgroundColor: "#b3e0ff" }}
                                >
                                  Edit
                                </Tooltip>
                              }
                            >
                              <span
                                className="material-icons color_brand"
                                onClick={() => onAction?.(row, "Edit")}
                                style={{ cursor: "pointer" }}
                              >
                                edit
                              </span>
                            </OverlayTrigger>
                          )}
                        </td>
                      );
                    }

                    if (header.key === "billingFor") {
                      return (
                        <td
                          key={header.key}
                          style={{
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px", // spacing between circle and text
                          }}
                        >
                          {/* Status Indicator */}
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor:
                                row.status === "Active" ? "#20AB3C" : "#7B7F81",
                              display: "inline-block",
                            }}
                          />

                          {/* Billing Type Text */}
                          {row.billingFor}
                        </td>
                      );
                    }
                    if (header.key === "priority") {
                      return (
                        <td
                          key={header.key}
                          style={{
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {row.priority}
                          {row.priority == "Stat" && (
                            <span className="material-symbols-outlined rushicon p-1">
                              sprint
                            </span>
                          )}
                        </td>
                      );
                    }

                    const isStatusColumn = header.key === "status";

                    const getStatusColor = (status: string) => {
                      switch (status) {
                        case "Active":
                          return "#20AB3C";
                        case "InActive":
                          return "#7B7F81";

                        default:
                          return "#000000";
                      }
                    };

                    const cellContent = row[header.key];

                    return (
                      <td
                        key={header.key}
                        style={{
                          padding: header.isNumeric
                            ? "8px 20px 8px 8px !important"
                            : "8px",
                          textAlign:
                            header.isNumeric && isStandardBilling
                              ? "right"
                              : header.isNumeric
                              ? "center"
                              : "left",
                        }}
                      >
                        <OverlayTrigger
                          placement="auto"
                          delay={{ show: 200, hide: 10 }}
                          overlay={
                            cellContent &&
                            cellContent?.length > (isLogTable ? 60 : 8) ? (
                              <Tooltip
                                id={`tooltip-${header.key}-${row.id}`}
                                style={{ backgroundColor: "#b3e0ff" }}
                              >
                                {cellContent}
                              </Tooltip>
                            ) : (
                              <></>
                            )
                          }
                        >
                          <span
                            // className= 'd-inline-block text-truncate'
                            className={`d-inline-block ${
                              !isLogTable ? "case-type-2line" : ""
                            }`}
                            style={{
                              maxWidth: isLogTable ? "none" : "90%",
                              cursor: isLogTable ? "default" : "pointer",
                              color: isStatusColumn
                                ? getStatusColor(cellContent)
                                : undefined,
                            }}
                          >
                            {cellContent}
                          </span>
                        </OverlayTrigger>
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={headers?.length} style={{ textAlign: "center" }}>
                  <span className="no_rec_data">No Record Found</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="recordList"
      >
        <Modal.Header closeButton className="d-flex">
          <Modal.Title className="fw-bold text-left modelheading offcanvas-title d-flex align-items-center">
            Overridden Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            {overrideData?.length ? (
              renderOverrideAccordion()
            ) : (
              <Row>
                <span className="no_rec_data">No Record Found</span>
              </Row>
            )}
          </>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default BillingDataTable;
